import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
  useDeferredValue,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { ChevronDown, Search, Plus } from "lucide-react";

const OVERLAY_EVENT = "pixelads:overlay:open";
const PROJECTS_REFRESH_EVENT = "pixelads:projects:refresh";

/**
 * Fire this after a successful import:
 * window.dispatchEvent(new CustomEvent("pixelads:project:imported", { detail: { projectId } }))
 *
 * Or set:
 * localStorage.setItem("pixelads:lastImportedProjectId", projectId)
 */
const PROJECT_IMPORTED_EVENT = "pixelads:project:imported";
const LAST_IMPORTED_KEY = "pixelads:lastImportedProjectId";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

  const FALLBACK_THUMB =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="96" height="64" viewBox="0 0 96 64">
      <rect width="96" height="64" rx="10" fill="#23233a"/>
      <rect x="1" y="1" width="94" height="62" rx="9" fill="none" stroke="rgba(255,255,255,0.08)"/>
    </svg>
  `);

function isUsableThumbnail(url) {
  if (!url || typeof url !== "string") return false;

  const v = url.trim().toLowerCase();

  if (!v) return false;
  if (v === "/placeholder.jpg") return false;
  if (v.endsWith("/placeholder.jpg")) return false;
  if (v.includes("placeholder.jpg")) return false;
  if (v === "null" || v === "undefined") return false;

  return true;
}

/** ---------- Virtualization constants ---------- */
const ITEM_HEIGHT = 52; // px, approx row height (padding + image)
const CONTAINER_MAX_H = 288; // Tailwind max-h-72 ≈ 288px
const OVERSCAN = 6; // extra rows above/below viewport

/** Row component: memoized to avoid re-renders */

const Row = memo(function Row({
  item,
  isSelected,
  onSelect,
  brokenThumbs,
  markThumbBroken,
}) {
  const thumbUrl =
    item?.id && !brokenThumbs[item.id] && isUsableThumbnail(item.thumbnail_url)
      ? item.thumbnail_url
      : "";

  return (
    <div
      className={`flex items-center px-4 sm:px-5 py-3 gap-4 cursor-pointer transition rounded-md mx-2 my-1
        ${
          isSelected
            ? "bg-gradient-to-r from-[#FC2783]/80 to-[#9C27B0]/70 text-white shadow-[0_0_0_1px_rgba(252,39,131,0.35)]"
            : "text-gray-200 hover:bg-[#2A2A40]/60 hover:text-white"
        }`}
      onClick={() => onSelect(item)}
      role="option"
      aria-selected={isSelected}
    >
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt=""
          className={`w-12 h-8 object-cover rounded-lg ring-1 ${
            isSelected ? "ring-white/50" : "ring-[#2A2A40]"
          }`}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_THUMB;
            markThumbBroken(item.id);
          }}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-12 h-8 rounded-lg bg-[#23233a] ring-1 ring-[#2A2A40]" />
      )}
      <div className="truncate font-semibold">{item.product_name || item.url}</div>
    </div>
  );
});

/** Virtualized list */
function VirtualizedList({
  items,
  selectedId,
  onSelect,
  brokenThumbs,
  markThumbBroken,
}) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const total = items.length;
  const viewportH = CONTAINER_MAX_H;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(viewportH / ITEM_HEIGHT) + OVERSCAN * 2;
  const endIndex = Math.min(total, startIndex + visibleCount);

  const topPad = startIndex * ITEM_HEIGHT;
  const bottomPad = Math.max(0, (total - endIndex) * ITEM_HEIGHT);

  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const handleSelect = useCallback(
    (item) => {
      onSelect(item);
    },
    [onSelect]
  );

  if (total === 0) {
    return (
      <div className="px-5 py-6 text-center text-gray-400 select-none">
        No matching products
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="max-h-72 overflow-auto will-change-transform"
      onScroll={onScroll}
    >
      {topPad > 0 && <div style={{ height: topPad }} />}

      {items.slice(startIndex, endIndex).map((item) => (
        <Row
          key={item.id}
          item={item}
          isSelected={selectedId === item.id}
          onSelect={handleSelect}
          brokenThumbs={brokenThumbs}
          markThumbBroken={markThumbBroken}
        />
      ))}

      {bottomPad > 0 && <div style={{ height: bottomPad }} />}
    </div>
  );
}

export default function Dropdown({
  label = "Select a Product",
  options = [],
  selected,
  onSelect,
}) {
  const { getToken } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [brokenThumbs, setBrokenThumbs] = useState({});
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const isOnAnalyze = useMemo(() => {
    const p = String(location?.pathname || "");
    return p === "/analyze" || p.startsWith("/analyze/");
  }, [location?.pathname]);

  // ✅ Live list that can refresh without needing a full page reload
  const [liveOptions, setLiveOptions] = useState(null);

  // ✅ Prevent repeated auto-select loops
  const lastAutoSelectedIdRef = useRef(null);

  // ✅ Track a "desired" product id (e.g. just imported) we should auto-select once available
  const desiredSelectIdRef = useRef(null);

  const isImportingSelected = useMemo(() => {
    const name = (selected?.product_name || "").toString().trim().toLowerCase();
    return name.includes("importing");
  }, [selected?.product_name]);

  const dispatchOverlayOpen = useCallback((source) => {
    try {
      window.dispatchEvent(
        new CustomEvent(OVERLAY_EVENT, { detail: { source } })
      );
    } catch {
      // ignore
    }
  }, []);
  
  const markThumbBroken = useCallback((id) => {
    if (!id) return;
    setBrokenThumbs((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: true };
    });
  }, []);

  const selectWithMeta = useCallback(
    (item, meta) => {
      // parent can optionally use meta.silent to avoid redirecting from /analyze
      onSelect?.(item, meta);
    },
    [onSelect]
  );

  const refreshProjects = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return null;

      const r = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = r?.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.projects)
        ? data.projects
        : [];

      if (Array.isArray(list)) {
        setLiveOptions(list);
        return list;
      }
      return null;
    } catch {
      return null;
    }
  }, [getToken]);

  // ✅ Refresh when the dropdown opens (covers "new project not visible until refresh")
  useEffect(() => {
    if (!isOpen) return;
    refreshProjects();
  }, [isOpen, refreshProjects]);

  // ✅ Refresh when the app signals "projects changed" (e.g. analyze finished)
  useEffect(() => {
    const onRefresh = () => {
      refreshProjects();
    };
    window.addEventListener(PROJECTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(PROJECTS_REFRESH_EVENT, onRefresh);
  }, [refreshProjects]);

  const effectiveOptions = useMemo(() => {
    if (Array.isArray(liveOptions) && liveOptions.length > 0) return liveOptions;
    return options || [];
  }, [liveOptions, options]);

  const hasSelectedInList = useMemo(() => {
    if (!selected?.id) return false;
    if (!Array.isArray(effectiveOptions) || effectiveOptions.length === 0)
      return false;
    return effectiveOptions.some((p) => p?.id === selected.id);
  }, [effectiveOptions, selected?.id]);

  const findById = useCallback((list, id) => {
    if (!id || !Array.isArray(list)) return null;
    return list.find((p) => p?.id === id) || null;
  }, []);

  const readLastImportedId = useCallback(() => {
    try {
      const v = localStorage.getItem(LAST_IMPORTED_KEY);
      return v ? String(v) : null;
    } catch {
      return null;
    }
  }, []);

  const clearLastImportedId = useCallback(() => {
    try {
      localStorage.removeItem(LAST_IMPORTED_KEY);
    } catch {
      // ignore
    }
  }, []);

  // ✅ Listen for "just imported" signals (event OR localStorage)
  useEffect(() => {
    const onImported = (e) => {
      const pid = e?.detail?.projectId ? String(e.detail.projectId) : null;
      if (!pid) return;
      desiredSelectIdRef.current = pid;
      try {
        localStorage.setItem(LAST_IMPORTED_KEY, pid);
      } catch {
        // ignore
      }
      window.dispatchEvent(new CustomEvent(PROJECTS_REFRESH_EVENT));
    };

    const onStorage = (e) => {
      if (e?.key !== LAST_IMPORTED_KEY) return;
      const pid = e?.newValue ? String(e.newValue) : null;
      if (!pid) return;
      desiredSelectIdRef.current = pid;
      window.dispatchEvent(new CustomEvent(PROJECTS_REFRESH_EVENT));
    };

    window.addEventListener(PROJECT_IMPORTED_EVENT, onImported);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(PROJECT_IMPORTED_EVENT, onImported);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // ✅ If we have a "desired" product id (just imported), pick it as soon as it appears
  useEffect(() => {
    const stored = readLastImportedId();
    if (stored) desiredSelectIdRef.current = stored;

    const desiredId = desiredSelectIdRef.current;
    if (!desiredId) return;

    // already selected? clear the desire
    if (selected?.id && String(selected.id) === String(desiredId)) {
      desiredSelectIdRef.current = null;
      clearLastImportedId();
      return;
    }

    // if list already contains it, select immediately
    const hit = findById(effectiveOptions, desiredId);
    if (hit) {
      selectWithMeta(hit, { source: "import", silent: isOnAnalyze });
      lastAutoSelectedIdRef.current = hit.id;
      desiredSelectIdRef.current = null;
      clearLastImportedId();
      return;
    }

    // otherwise poll briefly until it appears
    let mounted = true;
    let tries = 0;
    const MAX_TRIES = 30; // ~60s @ 2s

    const tick = async () => {
      tries += 1;
      const list = await refreshProjects();
      if (!mounted) return;

      const found = findById(list, desiredId);
      if (found) {
        selectWithMeta(found, { source: "import", silent: isOnAnalyze });
        lastAutoSelectedIdRef.current = found.id;
        desiredSelectIdRef.current = null;
        clearLastImportedId();
        return;
      }

      if (tries >= MAX_TRIES) return;
      window.setTimeout(tick, 2000);
    };

    tick();

    return () => {
      mounted = false;
    };
  }, [
    clearLastImportedId,
    effectiveOptions,
    findById,
    isOnAnalyze,
    readLastImportedId,
    refreshProjects,
    selectWithMeta,
    selected?.id,
  ]);

  // ✅ Auto-select behavior:
  // - If there is NO selection (or it's "Importing..."), pick first product
  // - If there IS a selection id but it isn't in list yet, do NOT override it immediately
  //   (prevents overriding the "just imported" selection before refresh completes)
  useEffect(() => {
    if (!Array.isArray(effectiveOptions) || effectiveOptions.length === 0) return;

    // valid selection exists and is not importing => keep it
    if (hasSelectedInList && !isImportingSelected) {
      lastAutoSelectedIdRef.current = selected?.id || null;
      return;
    }

    // if we have a selection id (non-importing) but it isn't in the list yet, wait
    if (selected?.id && !isImportingSelected && !hasSelectedInList) {
      return;
    }

    const first = effectiveOptions[0];
    const firstId = first?.id || null;
    if (!firstId) return;

    // prevent repeatedly selecting the same item over and over
    if (selected?.id === firstId && !isImportingSelected) {
      lastAutoSelectedIdRef.current = firstId;
      return;
    }
    if (lastAutoSelectedIdRef.current === firstId && !isImportingSelected) return;

    selectWithMeta(first, { source: "auto", silent: isOnAnalyze });
    lastAutoSelectedIdRef.current = firstId;
  }, [
    effectiveOptions,
    hasSelectedInList,
    isImportingSelected,
    isOnAnalyze,
    selectWithMeta,
    selected?.id,
  ]);

  // ✅ While selection says "Importing...", poll projects so the dropdown updates without reload.
  useEffect(() => {
    if (!isImportingSelected) return;

    let mounted = true;
    let tries = 0;
    const MAX_TRIES = 30; // ~60s @ 2s interval

    const tick = async () => {
      tries += 1;

      const list = await refreshProjects();
      if (!mounted) return;

      const hasList = Array.isArray(list) && list.length > 0;
      if (hasList) {
        // if we have a last imported id, prefer it
        const importedId = readLastImportedId();
        const importedHit = importedId ? findById(list, importedId) : null;
        if (importedHit) {
          selectWithMeta(importedHit, { source: "import", silent: isOnAnalyze });
          lastAutoSelectedIdRef.current = importedHit.id;
          desiredSelectIdRef.current = null;
          clearLastImportedId();
          return;
        }

        const stillImporting =
          (selected?.product_name || "").toString().toLowerCase().includes("importing");
        const selectedExists =
          !!selected?.id && list.some((p) => p?.id === selected.id);

        if (stillImporting || !selectedExists) {
          const first = list[0];
          if (first?.id && selected?.id !== first.id) {
            selectWithMeta(first, { source: "auto", silent: isOnAnalyze });
            lastAutoSelectedIdRef.current = first.id;
          }
        }
        return;
      }

      if (tries >= MAX_TRIES) return;
      window.setTimeout(tick, 2000);
    };

    tick();

    return () => {
      mounted = false;
    };
  }, [
    clearLastImportedId,
    findById,
    isImportingSelected,
    isOnAnalyze,
    readLastImportedId,
    refreshProjects,
    selectWithMeta,
    selected?.id,
    selected?.product_name,
  ]);

  // 1) Preprocess options (cache lowercased labels)
  const prepared = useMemo(() => {
    if (!effectiveOptions || effectiveOptions.length === 0) return [];
    return effectiveOptions.map((it) => {
      const lbl =
        (it.product_name && String(it.product_name)) ||
        (it.url && String(it.url)) ||
        "";
      return {
        ...it,
        __label: lbl,
        __labelLower: lbl.toLowerCase(),
      };
    });
  }, [effectiveOptions]);

  // 2) Defer search text to keep typing snappy
  const deferredSearch = useDeferredValue(search);
  const searchLower = deferredSearch.trim().toLowerCase();

  // 3) Fast filter
  const filtered = useMemo(() => {
    if (!searchLower) return prepared;
    return prepared.filter((it) => it.__labelLower.includes(searchLower));
  }, [prepared, searchLower]);

  const closeAndReset = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // 4) Outside click to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, { passive: true });
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Close product dropdown when another overlay opens (avatar / notifications)
  useEffect(() => {
    const onOverlayOpen = (e) => {
      const src = e?.detail?.source;
      if (src && src !== "productDropdown") closeAndReset();
    };
    window.addEventListener(OVERLAY_EVENT, onOverlayOpen);
    return () => window.removeEventListener(OVERLAY_EVENT, onOverlayOpen);
  }, [closeAndReset]);

  const toggleOpen = useCallback(() => {
    setIsOpen((o) => {
      const next = !o;
      if (next) dispatchOverlayOpen("productDropdown");
      return next;
    });
  }, [dispatchOverlayOpen]);

  const handleSelect = useCallback(
    (item) => {
      selectWithMeta(item, { source: "user", silent: false });
      closeAndReset();
    },
    [selectWithMeta, closeAndReset]
  );

  const goAnalyze = useCallback(() => {
    closeAndReset();
    navigate("/analyze");
  }, [navigate, closeAndReset]);

  return (
    <div ref={dropdownRef} className="relative w-full text-white text-sm font-semibold">
      <style>{`
        .dropdown-input,
        .dropdown-input:focus {
          outline: none !important;
          border: none !important;
          box-shadow: none !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          background: transparent !important;
          background-clip: padding-box !important;
        }
        .dropdown-input:-webkit-autofill,
        .dropdown-input:-webkit-autofill:hover,
        .dropdown-input:-webkit-autofill:focus,
        .dropdown-input:-internal-autofill-selected {
          -webkit-text-fill-color: #ffffff !important;
          transition: background-color 9999s ease-in-out 0s !important;
          box-shadow: 0 0 0px 1000px transparent inset !important;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
          caret-color: #ffffff !important;
          background: transparent !important;
        }
      `}</style>

      {/* Trigger */}
      <button
        onClick={toggleOpen}
        className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-[#15152B]
        border-white/25 hover:border-[#FC2783] focus:outline-none
        shadow-[0_2px_16px_0_rgba(252,39,131,0.08)] hover:shadow-[0_4px_24px_0_rgba(252,39,131,0.20)]
        ${isOpen ? "ring-2 ring-[#FC2783]/70 border-[#FC2783]" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3 truncate">
        {selected?.id &&
isUsableThumbnail(selected?.thumbnail_url) &&
!brokenThumbs[selected.id] ? (
  <img
    src={selected.thumbnail_url}
    alt=""
    className="w-12 h-8 object-cover rounded-lg ring-1 ring-[#2A2A40]"
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = FALLBACK_THUMB;
      markThumbBroken(selected.id);
    }}
    loading="lazy"
    decoding="async"
  />
) : (
  <div className="w-12 h-8 rounded-lg bg-[#23233a] ring-1 ring-[#2A2A40]" />
)}
          <span className="truncate text-white/95">
            {selected?.product_name || label}
          </span>
        </div>
        <ChevronDown
          size={20}
          className={`text-gray-300 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Menu */}
      {isOpen && (
        <div
          className="absolute left-0 z-50 mt-2 w-full rounded-xl overflow-hidden border shadow-2xl"
          style={{
            background: "linear-gradient(180deg, #1C1C2E 0%, #22223A 100%)",
            borderColor: "rgba(255,255,255,0.28)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#FC2783")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.borderColor = "rgba(255,255,255,0.28)")
          }
        >
          {/* Search */}
          <div className="sticky top-0 z-10 bg-[#1C1C2E] px-3 sm:px-4 py-3 border-b border-[#2A2A40]">
            <div
              className={`
                flex items-center gap-2 rounded-lg px-3 py-2 transition
                bg-[#111124]
                border border-transparent
                focus-within:border-[#FC2783] focus-within:ring-1 focus-within:ring-[#FC2783]
              `}
            >
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dropdown-input w-full bg-transparent text-white placeholder-gray-400 outline-none border-none shadow-none"
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          {/* Virtualized list */}
          <VirtualizedList
  items={filtered}
  selectedId={selected?.id}
  onSelect={handleSelect}
  brokenThumbs={brokenThumbs}
  markThumbBroken={markThumbBroken}
/>

          {/* Sticky bottom CTA */}
          <div className="sticky bottom-0 z-10 bg-[#1E1E33] border-t border-[#2A2A40] p-3">
            <button
              type="button"
              onClick={goAnalyze}
              className="w-full h-10 rounded-xl font-semibold whitespace-nowrap
                         bg-pink-600 hover:bg-pink-500 border border-pink-400/60 text-white transition"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Plus size={18} />
                Import product
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
